// Course send history page (renamed from Course Send Log). Chronological log
// of COURSE_SEND_LOG events with multi-select course / user / dept / location
// / status / outcome filters. Scope (metadata filters from the shell, incl.
// the course + user chips from the Courses page) applies before any local
// filters, so drilled-in scopes arrive pre-applied; the status filter can
// additionally be seeded by the drill context (e.g. the Completion Rate KPI
// lands here on Completed).
//
// Calculations: filter COURSE_SEND_LOG by scope + filters, sort by sentAt
// DESC. Score = correctAnswers/totalQuestions as a percentage. Outcome is
// derived from completion + score: Passed (completed & score >= 80), Failed
// (completed & score < 80), Retry (not completed & score populated), Expired
// (completed & no score). The raw event pull is capped server-side at the
// most-recent 20k rows; the table paginates in-memory within that.
//
// Postgres mapping:
//   - source table `course_sends` (send_id, user_id, course_id, sent_at,
//     completed_at, correct_answers, total_questions) joined to `users` for
//     the metadata dimensions.
//   - paginated query with server-side WHERE for scope + local filters.
//
// Integration hook: `useCourseHistoryQuery({ startDate, endDate, userEmail,
// status, outcome, courseName, limit, offset })` cache 10 minutes.
import React, { useState, useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { motion } from 'motion/react';
import { COURSE_SEND_LOG } from '../data';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';
import { FilterMulti, fmtLocal } from './SendHistoryPage';
import { courseScore, courseStatus, courseOutcome } from '../sendLogFormat';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

function applyScopeToCourseLog(log, scope) {
    const s = scope || {};
    return log.filter((e) => {
        if (!isIn(e.dept,       s.department)) return false;
        if (!isIn(e.location,   s.location))   return false;
        if (!isIn(e.city,       s.city))       return false;
        if (!isIn(e.country,    s.country))    return false;
        if (!isIn(e.company,    s.company))    return false;
        if (!isIn(e.state,      s.state))      return false;
        if (!isIn(e.adGroups,    s.adGroup))    return false;
        if (!isIn(e.courseName, s.course))     return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        return true;
    });
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Status + outcome vocabularies. Labels are shared verbatim between the filter
// dropdowns and the table badges so the two can never disagree; an empty
// dropdown selection means 'All'.
export const COURSE_STATUS_META = {
    Completed:   { bg: 'var(--green-bg)', text: 'var(--green-text)', border: 'rgba(102,187,106,.25)', dot: '#66BB6A' },
    Outstanding: { bg: 'var(--amber-bg)', text: 'var(--amber-text)', border: 'rgba(255,202,40,.3)',   dot: '#FFCA28' },
};
export const COURSE_STATUS_OPTIONS = ['Outstanding', 'Completed'];

const OUTCOME_META = {
    Passed:  { bg: 'var(--green-bg)', text: 'var(--green-text)', border: 'rgba(102,187,106,.25)', dot: '#66BB6A' },
    Failed:  { bg: 'var(--red-bg)',   text: 'var(--red-text)',   border: 'rgba(255,82,82,.25)',   dot: '#FF5252' },
    Retry:   { bg: 'var(--amber-bg)', text: 'var(--amber-text)', border: 'rgba(255,202,40,.3)',   dot: '#FFCA28' },
    Expired: { bg: 'action.hover',    text: 'text.secondary',    border: 'divider',               dot: 'text.disabled' },
};
export const OUTCOME_OPTIONS = ['Passed', 'Failed', 'Retry', 'Expired'];

// Quiz score as a whole-number percentage, or null when the attempt carries no
// score (no questions answered / unscored course).
function Chip({ meta, label }) {
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: '6px', py: '1px', borderRadius: '4px',
            fontSize: 10, fontWeight: 600,
            bgcolor: meta.bg, color: meta.text,
            border: '1px solid', borderColor: meta.border,
            whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: meta.dot, flexShrink: 0 }} />
            {label}
        </Box>
    );
}

export function CourseStatusBadge({ status }) {
    return <Chip meta={COURSE_STATUS_META[status] || COURSE_STATUS_META.Outstanding} label={status} />;
}

export function OutcomeBadge({ outcome }) {
    if (!outcome) return <Box component="span" sx={{ color: 'text.disabled' }}>&mdash;</Box>;
    return <Chip meta={OUTCOME_META[outcome] || OUTCOME_META.Expired} label={outcome} />;
}

// The canonical course-send column set, shared with the course send history
// card and course drill so they stay aligned. includeCourse drops the Course
// column when every row is already scoped to one course. Event timestamps
// sort on the raw ISO stamp and render in the viewer's local time zone.
export function courseLogColumns({ includeCourse = true } = {}) {
    return [
        { id: 'name',       label: 'Name',      always: true,    sortable: true, type: 'string', accessor: (e) => `${e.firstName} ${e.lastName}` },
        { id: 'email',      label: 'Email',     defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.email },
        ...(includeCourse ? [{ id: 'courseName', label: 'Course', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.courseName }] : []),
        { id: 'status',     label: 'Status',    defaultOn: true, sortable: true, type: 'string', accessor: (e) => courseStatus(e),
          render: (e) => <CourseStatusBadge status={courseStatus(e)} /> },
        { id: 'outcome',    label: 'Outcome',   defaultOn: true, sortable: true, type: 'string', accessor: (e) => courseOutcome(e) || '',
          render: (e) => <OutcomeBadge outcome={courseOutcome(e)} /> },
        { id: 'score',      label: 'Score',     defaultOn: true, sortable: true, type: 'number', accessor: (e) => { const s = courseScore(e); return s == null ? -1 : s; },
          render: (e) => { const s = courseScore(e); return s == null ? '' : `${s}%`; } },
        { id: 'sent',       label: 'Sent',      defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.sentAt || '',      render: (e) => fmtLocal(e.sentAt) },
        { id: 'completed',  label: 'Completed', defaultOn: true, sortable: true, type: 'string', accessor: (e) => e.completedAt || '', render: (e) => fmtLocal(e.completedAt) },
        { id: 'reminders',  label: 'Reminders', defaultOn: true, sortable: true, type: 'number', accessor: (e) => e.reminders ?? 0, align: 'right' },
        // Metadata: available via the column picker, off by default.
        { id: 'dept',       label: 'Department',  sortable: true, type: 'string', accessor: (e) => e.dept },
        { id: 'location',   label: 'Location',    sortable: true, type: 'string', accessor: (e) => e.location },
        { id: 'city',       label: 'City',        sortable: true, type: 'string', accessor: (e) => e.city },
        { id: 'country',    label: 'Country',     sortable: true, type: 'string', accessor: (e) => e.country },
        { id: 'state',      label: 'State',       sortable: true, type: 'string', accessor: (e) => e.state },
        { id: 'company',    label: 'Company',     sortable: true, type: 'string', accessor: (e) => e.company },
        { id: 'adGroup',    label: 'Group',       sortable: true, type: 'string', accessor: (e) => (Array.isArray(e.adGroups) ? e.adGroups.join(', ') : e.adGroup) },
        { id: 'courseType', label: 'Course Type', sortable: true, type: 'string', accessor: (e) => e.courseType || '' },
    ];
}


// Date window for the page, matching the topbar period selector. The full send
// log is the same data as the card that drills here, so it has to honour the
// same date range - otherwise "View all" from a 7-day view opened an all-time log.
const PERIOD_DAYS = { Today: 1, '7d': 7, '30d': 30, '90d': 90, '6m': 182, '12m': 365 };
function inPeriod(e, period, startDate, endDate) {
    const now = Date.now();
    const days = PERIOD_DAYS[period];
    const ws = days != null ? now - days * 86400000
        : (startDate ? Date.parse(startDate + 'T00:00:00Z') : now - 90 * 86400000);
    const we = days != null ? now
        : (endDate ? Date.parse(endDate + 'T23:59:59Z') : now);
    const ts = Date.parse((e.dateSent || e.sentAt || '') + 'T12:00:00Z');
    return Number.isFinite(ts) && ts >= ws && ts <= we;
}

// Re-exported so existing imports of these names keep resolving here.
export { courseScore, courseStatus, courseOutcome };

export default function CourseSendHistoryPage({ drill, scope, removeScope, initialStatus, period, startDate, endDate }) {
    const [filterCourses, setFilterCourses] = useState([]);
    const [filterUsers, setFilterUsers] = useState([]);
    const [filterDepts, setFilterDepts] = useState([]);
    // Seeded by drill context (e.g. the Completion Rate KPI lands here with
    // status=Completed); fully editable after landing.
    const [filterStatuses, setFilterStatuses] = useState(initialStatus ? [initialStatus] : []);
    const [filterOutcomes, setFilterOutcomes] = useState([]);
    const [filterLocations, setFilterLocations] = useState([]);

    const scopedLog = useMemo(
        () => applyScopeToCourseLog(COURSE_SEND_LOG, scope).filter((e) => inPeriod(e, period, startDate, endDate)),
        [scope, period, startDate, endDate],
    );

    const courses   = useMemo(() => [...new Set(scopedLog.map((e) => e.courseName))].sort(), [scopedLog]);
    const users     = useMemo(() => {
        const m = new Map();
        for (const e of scopedLog) if (!m.has(e.email)) m.set(e.email, `${e.firstName} ${e.lastName}`.trim() || e.email);
        return [...m.entries()].map(([email, name]) => ({ email, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [scopedLog]);
    const depts     = useMemo(() => [...new Set(scopedLog.map((e) => e.dept))].sort(), [scopedLog]);
    const locations = useMemo(() => [...new Set(scopedLog.map((e) => e.location))].sort(), [scopedLog]);

    const rows = useMemo(() => scopedLog.filter((e) => {
        if (filterCourses.length   && !filterCourses.includes(e.courseName))             return false;
        if (filterUsers.length     && !filterUsers.some((u) => u.email === e.email))     return false;
        if (filterDepts.length     && !filterDepts.includes(e.dept))                     return false;
        if (filterStatuses.length  && !filterStatuses.includes(courseStatus(e)))         return false;
        if (filterOutcomes.length  && !filterOutcomes.includes(courseOutcome(e)))        return false;
        if (filterLocations.length && !filterLocations.includes(e.location))             return false;
        return true;
    }), [scopedLog, filterCourses, filterUsers, filterDepts, filterStatuses, filterOutcomes, filterLocations]);

    const hasFilters = filterCourses.length || filterUsers.length || filterDepts.length || filterStatuses.length || filterOutcomes.length || filterLocations.length;

    const columns = courseLogColumns();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Filters sit above the table on their own line, same chip control and
                placement as every other page, rather than inside the table header. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <FilterMulti value={filterCourses}   onChange={setFilterCourses}   options={courses}   placeholder="All courses" />
                    <FilterMulti value={filterUsers}     onChange={setFilterUsers}     options={users}     placeholder="All users" getLabel={(u) => `${u.name} (${u.email})`} />
                    <FilterMulti value={filterDepts}     onChange={setFilterDepts}     options={depts}     placeholder="All departments" />
                    <FilterMulti value={filterLocations} onChange={setFilterLocations} options={locations} placeholder="All locations" />
                    <FilterMulti value={filterStatuses}  onChange={setFilterStatuses}  options={COURSE_STATUS_OPTIONS} placeholder="All statuses" />
                    <FilterMulti value={filterOutcomes}  onChange={setFilterOutcomes}  options={OUTCOME_OPTIONS} placeholder="All outcomes" />
                {hasFilters ? (
                    <Button size="small" onClick={() => { setFilterCourses([]); setFilterUsers([]); setFilterDepts([]); setFilterStatuses([]); setFilterOutcomes([]); setFilterLocations([]); }}
                        sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                        Clear
                    </Button>
                ) : null}
            </Box>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="Course Send History"
                    columns={columns}
                    rows={rows}
                    rowKey={(e) => `${e.email}-${e.courseName}-${e.sentAt || e.dateSent}`}
                    onRowClick={(e) => drill('user-detail', `${e.firstName} ${e.lastName}`, { type: 'user', value: e.email })}
                    initialSort={{ by: 'sent', dir: 'desc' }}
                    storageKey="rbx-course-send-history-cols-v1"
                    emptyMessage="No matching course deliveries"
                />
            </motion.div>
        </motion.div>
    );
}
